import { computed, type MaybeRefOrGetter, toValue } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import type { ChannelPermission } from "@f3/shared/permissions";
import { useGatingPermissions } from "../use-gating-permissions";
import type { AuthorizationRuleForGating } from "../use-gating-permissions";
import type { ExperienceMetadata } from "@/models";
import {
  ChannelEventType,
  useChannelEvents,
} from "@/composables/channel-events";
import { useGatingEvaluator } from "./use-gating-evaluator";
import { GatingAccessType } from "../gating-types";

export function canPerformGatedAction({
  isReady,
  userPermissionMask,
  rule,
  isPermissionRequired,
  matchRule,
}: {
  isReady: boolean;
  userPermissionMask: number | null;
  rule: AuthorizationRuleForGating | null;
  isPermissionRequired: boolean;
  matchRule: (rule: AuthorizationRuleForGating) => boolean;
}): boolean {
  if (!isReady || userPermissionMask === null) return false;
  if (!rule) return true;
  if (!isPermissionRequired) return true;

  return matchRule(rule);
}

export function useGatingForAction<T extends readonly unknown[]>(
  permission: MaybeRefOrGetter<ChannelPermission>,
  action: (...args: readonly [...T]) => void = () => {},
  options?: {
    onDenied?: (...args: readonly [...T]) => void;
    gatingExperience?: MaybeRefOrGetter<ExperienceMetadata>;
  },
) {
  const channelStore = useChannelStore();
  const { matchRule, getRelevantRule, getGatingRuleMetadata, isReady } =
    useGatingEvaluator();
  const { authorizationRuleGroup } = useGatingPermissions();
  const { emitChannelEvent } = useChannelEvents();

  const hasPermission = computed(() => {
    const userPermissionMask = channelStore.permissions.permissionMask;

    // If the gating evaluator is not ready or user is not logged in, deny access
    if (!isReady.value || userPermissionMask === null) {
      return false;
    }

    const permissionValue = toValue(permission);
    const rule = getRelevantRule(
      toValue(authorizationRuleGroup.value),
      permissionValue,
    );

    // If there is no specific rule for this permission, we can assume the user has it
    if (!rule) return true;

    const ruleMetadata = getGatingRuleMetadata(rule);
    const isPermissionRequired =
      !!ruleMetadata && ruleMetadata.accessType === GatingAccessType.limited;

    // If the permission is not required (public), we can assume the user has it
    if (!isPermissionRequired) return true;

    return canPerformGatedAction({
      isReady: isReady.value,
      userPermissionMask,
      rule,
      isPermissionRequired,
      matchRule,
    });
  });

  function handler(...args: readonly [...T]) {
    if (options?.gatingExperience) {
      const gatingExperience = toValue(options.gatingExperience);

      channelStore.$setGatingExperience({
        experienceId: gatingExperience.experienceId,
        experienceTitle: gatingExperience.experienceTitle,
        experienceType: gatingExperience.experienceType,
        experienceAction: gatingExperience.experienceAction,
        experienceCreatorId: gatingExperience.experienceCreatorId,
        experienceCreatorUsername: gatingExperience.experienceCreatorUsername,
        experienceCommunityIds: gatingExperience.experienceCommunityIds,
        experienceCommunityNames: gatingExperience.experienceCommunityNames,
        experienceMembership: gatingExperience.experienceMembership,
      });
    }
    if (!hasPermission.value) {
      const maybeEvent = args[0];
      if (maybeEvent instanceof Event) {
        maybeEvent.preventDefault();
        maybeEvent.stopPropagation();
      }

      options?.onDenied?.(...args);

      const relevantRule = getRelevantRule(
        toValue(authorizationRuleGroup.value),
        toValue(permission),
      );

      emitChannelEvent({
        type: ChannelEventType.SHOW_AUTH_MODAL,
        requestedPermission: toValue(permission),
        requestedAuthorizationRule: relevantRule ?? undefined,
      });
    } else {
      action(...args);
    }
  }

  function silentHandler(...args: readonly [...T]) {
    if (options?.gatingExperience) {
      const gatingExperience = toValue(options.gatingExperience);

      channelStore.$setGatingExperience({
        experienceId: gatingExperience.experienceId,
        experienceTitle: gatingExperience.experienceTitle,
        experienceType: gatingExperience.experienceType,
        experienceAction: gatingExperience.experienceAction,
        experienceCreatorId: gatingExperience.experienceCreatorId,
        experienceCreatorUsername: gatingExperience.experienceCreatorUsername,
        experienceCommunityIds: gatingExperience.experienceCommunityIds,
        experienceCommunityNames: gatingExperience.experienceCommunityNames,
        experienceMembership: gatingExperience.experienceMembership,
      });
    }

    if (!hasPermission.value) {
      const maybeEvent = args[0];
      if (maybeEvent instanceof Event) {
        maybeEvent.preventDefault();
        maybeEvent.stopPropagation();
      }

      console.log("[Gating] Denied action", permission);
      options?.onDenied?.(...args);
    } else {
      console.log("[Gating] Accepted action", permission);
      action(...args);
    }
  }

  function triggerPopupModal(permission: ChannelPermission) {
    const relevantRule = getRelevantRule(
      toValue(authorizationRuleGroup.value),
      permission,
    );

    emitChannelEvent({
      type: ChannelEventType.SHOW_AUTH_MODAL,
      requestedPermission: permission,
      requestedAuthorizationRule: relevantRule ?? undefined,
    });
  }

  return {
    hasPermission,
    handler,
    silentHandler,
    action,
    triggerPopupModal,
  };
}
